import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, Alert } from '@mui/material';
import SchoolProfileData from '../components/SchoolProfileData';
import SchoolProfileCover from '../components/SchoolProfileCover';
import SchoolProfileHello from '../components/SchoolProfileHello';
import SchoolProfilePhilosophy from '../components/SchoolProfilePhilosophy';
import SchoolProfilePlaceholder from '../components/SchoolProfilePlaceholder';
import SchoolProfileSkeleton from '../components/SchoolProfileSkeleton';
import { ProfileContext } from '../context/ProfileContext';
import axiosInstance from '../axiosConfig';
import API_URL from '../config';

const SchoolProfilePage = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [schoolProfile, setSchoolProfile] = useState(null);
  const [schoolVerified, setSchoolVerified] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schoolVerificationDocument, setSchoolVerificationDocument] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [alert, setAlert] = useState(null);
  const { setUserProfile } = React.useContext(ProfileContext);
  const [isSchoolProfileComplete, setIsSchoolProfileComplete] = useState(false);

  const handleTabChange = (event, newValue) => {
    if (isEditing) {
      // Warnung: Bearbeitungsmodus verlassen ohne zu speichern
      setAlert({
        severity: "warning",
        message: "Sie haben den Editiermodus verlassen, ohne zu speichern. Ihre Änderungen wurden verworfen."
      });
      setIsEditing(false); // Wir gehen raus aus dem Editiermodus
    }
    setTabIndex(newValue);
  };

  useEffect(() => {
    if (alert && alert.severity !== "info") {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 3000); // 3 Sekunden Timeout
  
      return () => clearTimeout(timer); // Cleanup-Funktion für den Timer
    }
  }, [alert]);

  const updateUserProfileCompleteStatus = async (isComplete) => {
    const token = localStorage.getItem('authToken');
    try {
      await axiosInstance.patch(
        '/profile/set-school-profile-complete',
        { schoolProfileComplete: isComplete },
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Benutzerprofils:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');

    // Abrufen des Nutzerprofils
    const fetchUserProfile = axiosInstance
      .get('/profile/user-profile', {
        headers: {
          Authorization: `${token}`,
        },
      })
      .then((response) => {
        if (response.data.profile) {
          setSchoolVerified(response.data.profile.schoolVerified);
          setSchoolVerificationDocument(response.data.profile.schoolVerificationDocument);
        }
      })
      .catch((error) => {
        console.error('Fehler beim Abrufen des Nutzerprofils:', error);
      });

    // Abrufen des Schulprofils
    const fetchSchoolProfile = axiosInstance
      .get('/profile/school-profile', {
        headers: {
          Authorization: `${token}`,
        },
      })
      .then((response) => {
        if (response.data.profile) {
          setSchoolProfile(response.data.profile);

        // Prüfen und Status aktualisieren
        const isComplete = isProfileComplete(response.data.profile);
        setIsSchoolProfileComplete(isComplete);
        updateUserProfileCompleteStatus(isComplete);
        setUserProfile((prevProfile) => ({
          ...prevProfile,
          schoolProfileComplete: isComplete,
        }));
        }
      })
      .catch((error) => {
        console.error('Fehler beim Abrufen des Schulprofils:', error);
      });

      Promise.all([fetchUserProfile, fetchSchoolProfile]).finally(() => {
        setLoading(false); // Ladezustand beenden
      })
  }, []);

  const isProfileComplete = (profile) => {
    return !!(
      profile?.name &&
      profile?.street &&
      profile?.postalCode &&
      profile?.city &&
      profile?.contactEmail &&
      profile?.contactPhone &&
      profile?.motto &&
      profile?.schoolHelloMessage &&
      profile?.schoolManager &&
      profile?.philosophyMessage
    );
  };

  const tabTitles = ["Schulprofildaten", "Deckblatt", "Grußwort", "Schulphilosophie"];

  const handlePartialProfileUpdate = (updatedPartialProfile) => {
    if (updatedPartialProfile?.type === "editStart") {
      setIsEditing(true);
      // Info-Alert
      setAlert({
        severity: "info",
        message: "Sie bearbeiten gerade das Schulprofil. Vergessen Sie nicht zu speichern!"
      });
      return;
    } else if (updatedPartialProfile?.type === "saveSuccess") {
      // Profil updaten, da wir es von response.data.profile bekommen haben
      const profileData = updatedPartialProfile.profile || {};
      setSchoolProfile((prevProfile) => {
        const updatedProfile = {
          ...prevProfile,
          ...profileData, // Teil-Updates mit dem bestehenden Profil zusammenführen
        };
    
        // Prüfe, ob das Profil nach dem Update vollständig ist
        const isComplete = isProfileComplete(updatedProfile);
        updateUserProfileCompleteStatus(isComplete);
    
        // Aktualisiere den Benutzerstatus
        setUserProfile((prevUserProfile) => ({
          ...prevUserProfile,
          schoolProfileComplete: isComplete,
        }));
    
        return updatedProfile;
      });
      setIsEditing(false);
  
      setAlert({
        severity: "success",
        message: "Die Änderungen wurden erfolgreich gespeichert!"
      });
      return;
    } else if (updatedPartialProfile?.type === "saveError") {
      setIsEditing(false);
      setAlert({
        severity: "error",
        message: "Beim Speichern ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder wenden Sie sich an info@schullogbuch24.de."
      });
      return;
    } else {
      setSchoolProfile((prevProfile) => {
        const mergedProfile = { ...prevProfile, ...updatedPartialProfile };
        
        setIsEditing(true); // Nutzer hat etwas geändert
        // Info-Alert, falls gewünscht; ansonsten kann man es hier weglassen,
        // weil wir "editStart" schon abfangen. 
        // setAlert({
        //   severity: "info",
        //   message: "Sie bearbeiten gerade das Schulprofil. Vergessen Sie nicht zu speichern!"
        // });
    
        return mergedProfile;
      });
    }
  };
  
  if (loading) {
    return <SchoolProfileSkeleton />
  }

  if (!schoolVerified) {
    return <SchoolProfilePlaceholder schoolVerificationDocument={schoolVerificationDocument} />;
  }

  return (
    <Box sx={{ pt: '2.6rem', maxWidth: 'calc(700px + 2rem)' }}>
      <Box sx={{ border: '1px solid #ccc', borderRadius: '8px', ml: '2rem'}}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50px',
            borderBottom: '2px solid #e0e0e0',
            backgroundColor: '#f9f9f9',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <Typography
            variant="h5" // Etwas kleiner für bessere Integration
            sx={{
              color: '#333', // Dezenter Farbton
              letterSpacing: '0.5px' // Leichte Abstände für mehr Eleganz
            }}
          >
            {tabTitles[tabIndex]}
          </Typography>
        </Box>

        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="School Profile Tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ marginLeft: '2rem' }}
        >
        {/* <Tabs value={tabIndex} onChange={handleTabChange} aria-label="School Profile Tabs"> */}
          <Tab label="Schulprofildaten" />
          <Tab label="Deckblatt" />
          <Tab label="Grußwort" />
          <Tab label="Schulphilosophie" />
        </Tabs>

        {!isProfileComplete(schoolProfile) && (
          <Box sx={{ marginLeft: '2rem', marginRight: '2rem', marginTop: '1rem', maxWidth: '700px' }}>
            <Alert severity="error">
              Die Schulprofildaten inkl. Deckblatt, Grußwort und Schulphilosophie sind noch nicht vollständig ausgefüllt. Nachdem diese Daten vollständig ausgefüllt wurden, wird der Konfigurator für Sie freigeschaltet.
            </Alert>
          </Box>
        )}
        {alert && (
          <Box sx={{ marginLeft: '2rem', marginTop: '1rem', maxWidth: '700px', mr: '2rem' }}>
            <Alert severity={alert.severity}>
              {alert.message}
            </Alert>
          </Box>
        )}

        <Box sx={{ paddingLeft: '1rem', paddingTop: '1rem' }}>
          {tabIndex === 0 && <SchoolProfileData onUpdate={handlePartialProfileUpdate}/>}
          {tabIndex === 1 && <SchoolProfileCover onUpdate={handlePartialProfileUpdate}/>}
          {tabIndex === 2 && <SchoolProfileHello onUpdate={handlePartialProfileUpdate}/>}
          {tabIndex === 3 && <SchoolProfilePhilosophy onUpdate={handlePartialProfileUpdate}/>}
        </Box>
      </Box>
    </Box>
  );
};

export default SchoolProfilePage;
